import React, { useState } from 'react';
import { X, FileText, Calendar, DollarSign, User, Building, Package, CreditCard, CheckCircle, XCircle, Tag, Trash2, AlertTriangle, Globe, Home, Clock, Car } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatINR } from '../utils/currency';

const formatCurrency = (amount, currency = 'INR') => {
  if (currency === 'USD') return `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'EUR') return `€${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'GBP') return `£${Number(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'OTHERS') return `${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (Other)`;
  return formatINR(amount);
};

const ExpenseModal = ({ expense, onClose, onApprove, onMarkPaid, onDelete, showActions, actionType }) => {
  const { user } = useAuth();
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleApprove = async (status) => {
    if (status === 'rejected' && (!remarks || remarks.trim() === '')) {
      setError('Rejection reason is mandatory');
      return;
    }
    setProcessing(true);
    setError('');
    try {
      await onApprove(expense.id, status, remarks);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update expense');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkPaid = async () => {
    setProcessing(true);
    await onMarkPaid(expense.id);
    setProcessing(false);
  };

  const handleDelete = async () => {
    setProcessing(true);
    setError('');
    try {
      await onDelete(expense.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete expense');
      setProcessing(false);
    }
  };

  const getApprovalStatusBadge = (status) => {
    const map = {
      pending: { cls: 'status-pending', label: 'Pending' },
      approved: { cls: 'status-approved', label: 'Approved' },
      auto_approved: { cls: 'status-approved', label: 'Approved' },
      rejected: { cls: 'status-rejected', label: 'Rejected' }
    };
    const s = map[status] || map.pending;
    return <span className={`status-badge ${s.cls}`}>{s.label}</span>;
  };

  const getSettlementStatusBadge = (settlementStatus) => {
    if (settlementStatus === 'reimbursed') return <span className="status-badge status-paid">Reimbursed</span>;
    if (settlementStatus === 'reimbursement_pending') return <span className="status-badge status-pending">Reimbursement Pending</span>;
    if (settlementStatus === 'company_paid') return <span className="status-badge status-approved">Company Paid</span>;
    if (settlementStatus === 'not_applicable') return <span className="status-badge bg-gray-50 text-gray-600 border-gray-200">N/A</span>;
    return null;
  };

  const getPaymentModeLabel = (mode) => {
    const colorMap = {
      'Personal': 'bg-blue-50 text-blue-700',
      'Company Cash': 'bg-emerald-50 text-emerald-700',
      'Company Card': 'bg-teal-50 text-teal-700'
    };
    return <span className={`text-xs font-medium px-2 py-1 rounded ${colorMap[mode] || ''}`}>{mode === 'Personal' ? 'Personal (Reimbursement)' : mode}</span>;
  };

  const shouldShowInternalDetails = user?.role === 'admin' || user?.role === 'accounts' || user?.role === 'approver';

  const DetailRow = ({ icon: Icon, label, children }) => (
    <div className="flex items-start gap-3 py-2.5">
      <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-gray-400" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-gray-400 mb-0.5">{label}</div>
        <div className="text-sm font-medium text-gray-900">{children}</div>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="expense-modal">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex justify-between items-start">
          <div>
            <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Expense Details
            </h2>
            <div className="flex gap-2 mt-2 flex-wrap">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                expense.expense_type === 'INTERNATIONAL'
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
              }`}>{expense.expense_type || 'DOMESTIC'}</span>
              <span className="text-[10px] text-gray-400 uppercase self-center">Approval:</span>
              {getApprovalStatusBadge(expense.approval_status)}
              {expense.current_approval_step && expense.current_approval_step !== 'done' && expense.approval_status === 'pending' && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 font-medium">
                  @ {({dgm: 'DGM', senior_approver: 'Sr. Approver', management: 'Management', hr: 'HR'})[expense.current_approval_step] || expense.current_approval_step?.toUpperCase()}
                </span>
              )}
              <span className="text-[10px] text-gray-400 uppercase self-center ml-1">Settlement:</span>
              {getSettlementStatusBadge(expense.settlement_status)}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-testid="close-modal-button">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <DetailRow icon={User} label="Employee">
              {expense.employee_name}
              {expense.submitted_by_user_name !== expense.employee_name && (
                <span className="text-xs text-gray-400 ml-1">submitted by {expense.submitted_by_user_name}</span>
              )}
            </DetailRow>
            <DetailRow icon={DollarSign} label="Amount">
              <span className="text-lg font-bold">{formatCurrency(expense.amount, expense.currency || 'INR')}</span>
              <span className="text-xs text-gray-400 ml-1">{expense.currency || 'INR'}</span>
            </DetailRow>
            <DetailRow icon={Building} label="Department">
              {expense.department}
            </DetailRow>
            <DetailRow icon={Calendar} label="Expense Date">
              {new Date(expense.expense_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </DetailRow>
            <DetailRow icon={Tag} label="Brand / Division">
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">
                {expense.brand_division || 'N/A'}
              </span>
            </DetailRow>
            <DetailRow icon={CreditCard} label="Payment Mode">
              {getPaymentModeLabel(expense.payment_mode)}
            </DetailRow>
            <DetailRow icon={Package} label="Category">
              {expense.category}
            </DetailRow>
            <DetailRow icon={FileText} label="Vendor">
              {expense.vendor}
            </DetailRow>
          </div>

          {/* Trip info */}
          {expense.trip_name && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-xs text-blue-700 font-medium">Linked to Trip: {expense.trip_name}</div>
            </div>
          )}

          {/* Personal Vehicle km info */}
          {expense.category === 'Personal Vehicle' && expense.kilometres_travelled && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="personal-vehicle-info">
              <div className="flex items-center gap-2 mb-1">
                <Car className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">Personal Vehicle</span>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <div className="text-xs text-amber-600">Kilometres</div>
                  <div className="text-sm font-bold text-amber-900">{expense.kilometres_travelled} km</div>
                </div>
                <div>
                  <div className="text-xs text-amber-600">Rate/km</div>
                  <div className="text-sm font-bold text-amber-900">₹{expense.per_km_rate_used}</div>
                </div>
                <div>
                  <div className="text-xs text-amber-600">Total</div>
                  <div className="text-sm font-bold text-amber-900">{formatCurrency(expense.amount, expense.currency || 'INR')}</div>
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="text-xs text-gray-400 mb-1.5">Description</div>
            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-800">{expense.description}</div>
          </div>

          {/* Approval info */}
          {expense.approved_by && (expense.approval_status === 'approved' || expense.approval_status === 'auto_approved') && (
            <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="text-sm text-emerald-700 font-medium">Approved by: {expense.approved_by}</div>
              {expense.approval_remarks && shouldShowInternalDetails && (
                <div className="text-xs text-emerald-600 mt-0.5">Remarks: {expense.approval_remarks}</div>
              )}
            </div>
          )}

          {expense.approval_status === 'rejected' && expense.approval_remarks && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="text-sm text-red-700 font-medium">Rejected by: {expense.approved_by}</div>
              <div className="text-xs text-red-600 mt-0.5">Reason: {expense.approval_remarks}</div>
            </div>
          )}

          {/* Approval History */}
          {expense.approval_history && expense.approval_history.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-xs text-gray-400 mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Approval History</div>
              <div className="space-y-2">
                {expense.approval_history.map((entry, idx) => (
                  <div key={idx} className={`p-2 rounded-lg text-xs ${
                    entry.action === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                  }`}>
                    <span className="font-semibold">{entry.by}</span> ({entry.role})
                    {entry.action === 'approved' ? ' approved' : ' rejected'} at {entry.step?.toUpperCase()} step
                    {entry.remarks && <span className="block mt-0.5 text-gray-500">"{entry.remarks}"</span>}
                    <span className="block text-gray-400 text-[10px]">{entry.at ? new Date(entry.at).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ledger info for international */}
          {expense.ledger_deducted && (
            <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="text-xs text-emerald-700">Deducted from Company {expense.ledger_account_type} ledger</div>
            </div>
          )}

          {expense.settlement_status === 'company_paid' && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-xs text-blue-700">Paid using {expense.payment_mode}. No reimbursement required.</div>
            </div>
          )}

          {expense.settlement_date && expense.settlement_status === 'reimbursed' && (
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="text-xs text-purple-700">Reimbursed on: {new Date(expense.settlement_date).toLocaleDateString()}</div>
            </div>
          )}

          {/* Bill */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="text-xs text-gray-400 mb-2">Uploaded Bill</div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}${expense.bill_file_url}?token=${localStorage.getItem('token')}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 text-sm font-medium transition-colors"
              data-testid="view-bill-link"
            >
              <FileText className="w-4 h-4" />View Bill
            </a>
          </div>

          {/* Actions */}
          {showActions && actionType === 'approval' && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remarks {<span className="text-xs text-gray-400">(required for rejection)</span>}
                </label>
                <textarea value={remarks} onChange={(e) => { setRemarks(e.target.value); setError(''); }}
                  className={`input-field h-16 resize-none ${error ? 'border-red-400' : ''}`}
                  placeholder="Add comments or reason..." data-testid="remarks-input"
                />
                {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
              </div>
              <div className="flex gap-3">
                <button onClick={() => handleApprove('approved')} disabled={processing}
                  className="btn btn-primary flex-1" data-testid="approve-button"
                >
                  <CheckCircle className="w-4 h-4" />{processing ? 'Processing...' : 'Approve'}
                </button>
                <button onClick={() => handleApprove('rejected')} disabled={processing}
                  className="btn btn-danger flex-1" data-testid="reject-button"
                >
                  <XCircle className="w-4 h-4" />{processing ? 'Processing...' : 'Reject'}
                </button>
              </div>
            </div>
          )}

          {showActions && actionType === 'payment' && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <button onClick={handleMarkPaid} disabled={processing}
                className="btn btn-primary w-full justify-center" data-testid="mark-paid-button"
              >
                <CheckCircle className="w-4 h-4" />{processing ? 'Processing...' : 'Mark as Reimbursed'}
              </button>
            </div>
          )}

          {/* Admin-only Delete */}
          {user?.role === 'admin' && onDelete && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
                  data-testid="delete-expense-trigger"
                >
                  <Trash2 className="w-3.5 h-3.5" />Delete this expense
                </button>
              ) : (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="delete-confirm-dialog">
                  <div className="flex items-start gap-3 mb-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Are you sure you want to delete this expense?</p>
                      <p className="text-xs text-red-600 mt-1">
                        {formatCurrency(expense.amount, expense.currency || 'INR')} &middot; {expense.vendor} &middot; {expense.employee_name}
                      </p>
                    </div>
                  </div>
                  {error && <p className="text-red-600 text-xs mb-2">{error}</p>}
                  <div className="flex gap-2">
                    <button onClick={handleDelete} disabled={processing}
                      className="btn btn-danger text-xs px-3 py-1.5" data-testid="confirm-delete-button"
                    >
                      <Trash2 className="w-3.5 h-3.5" />{processing ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                    <button onClick={() => setShowDeleteConfirm(false)}
                      className="btn btn-secondary text-xs px-3 py-1.5" data-testid="cancel-delete-button"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpenseModal;
